import { BUNDLE_COMPATIBILITY_MESSAGE } from './BundleCompatibility';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { observable } from 'mobx';
import { logicNodeRegistry } from './LogicNodeRegistry';
import { getEffectiveSideIO } from '@/models/tiles/TileDefinition';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { TileSideIOType } from '@/schemas/tiles';
import { MAX_LOWERED_GRAPH_ITEMS } from './graphBudgets';
import type { IConnectionModel, ITileManagerModel } from '@/interfaces/models';
import type { TileInstance } from '@/models/tiles/TileInstance';
import type { LogicGraph } from './LogicGraph';
import { Side } from '@/schemas/tiles';
import { Point } from '@/models/Point';
import { getGlobalDirection, getLocalSide, getNeighborPosition } from './utils';
import { opposite } from '@/utils/direction';

type Vertex = {
  key: string;
  tile: TileInstance;
  sides: Side[];
  channel?: string;
  runtime?: string;
  nodes: Map<string, NodeHandle>;
  adapter?: boolean;
  scalarNodes?: Set<NodeHandle>;
  virtual?: boolean;
  embedded?: {
    node: NodeHandle;
    path: string[];
    labels?: string[];
    ids: string[][];
    selectors: string[][];
  }[];
};

/** Labels are discovered from physical cable topology; IDs alone never add edges. */
export function buildCountFreeCableLanes(
  graph: LogicGraph,
  manager: ITileManagerModel,
  connections?: IConnectionModel,
) {
  const tiles = manager.tiles;
  const strict = tiles[0]?.context?.userPreferences?.bundleChipIdentityEnabled === true;
  const displayLabels = new Map<string, string>();
  const memberKey = (tile: TileInstance, id: string) => {
    const label = tile.adapterSignalLabels?.[id] ?? '?';
    displayLabels.set(id, label);
    return strict ? id : label;
  };
  const displayPath = (path: string[]) =>
    (strict ? path.map((key) => displayLabels.get(key) ?? key) : path).join(' / ');
  if (
    !tiles.some(
      (tile) =>
        tile.adapterPort ||
        (tile.meta.partOfChip &&
          Object.values(tile.laneNodeIds ?? {})
            .flat()
            .some((id) => graph.nodes.get(id)?.bundlePath?.length)),
    )
  )
    return;
  // Adapter-authored pins already have lane identity. They are not anonymous
  // scalar driver provenance, even though their lowered nodes use WIRE.
  const selectedLabels = new Map<NodeHandle, string>();
  const selectedTiles = new Map<NodeHandle, TileInstance>();
  for (const tile of tiles) {
    if (!tile.adapterPort) continue;
    (tile.laneNodeIds?.[Side.LEFT] ?? []).forEach((node, index) => {
      const id = tile.adapterSignalIds?.[index];
      const label = id && memberKey(tile, id);
      if (label) {
        selectedLabels.set(node, label);
        selectedTiles.set(node, tile);
      }
    });
  }
  const scalarParent = new Map(
    [...graph.nodes.values()]
      .filter((node) => node.type === 'WIRE' && !selectedLabels.has(node.id))
      .map((node) => [node.id, node.id]),
  );
  const scalarRoot = (id: NodeHandle): NodeHandle => {
    let root = id;
    while (scalarParent.get(root) !== root) root = scalarParent.get(root)!;
    while (id !== root) {
      const next = scalarParent.get(id)!;
      scalarParent.set(id, root);
      id = next;
    }
    return root;
  };
  for (const edge of graph.edges.values())
    if (scalarParent.has(edge.sourceNodeId) && scalarParent.has(edge.targetNodeId))
      scalarParent.set(scalarRoot(edge.targetNodeId), scalarRoot(edge.sourceNodeId));
  const scalarComponents = new Map<NodeHandle, Set<NodeHandle>>();
  for (const id of scalarParent.keys()) {
    const root = scalarRoot(id),
      nodes = scalarComponents.get(root) ?? new Set<NodeHandle>();
    nodes.add(id);
    scalarComponents.set(root, nodes);
  }
  const vertices: Vertex[] = [];
  const byTile = new Map<string, Map<Side, Vertex>>();
  const add = (vertex: Vertex) => {
    vertices.push(vertex);
    let sides = byTile.get(vertex.tile.id);
    if (!sides) byTile.set(vertex.tile.id, (sides = new Map()));
    for (const side of vertex.sides) sides.set(side, vertex);
  };
  for (const tile of tiles) {
    tile.cablePort = false;
    if (tile.adapterPort)
      add({
        key: `${tile.id}:adapter`,
        tile,
        sides: [Side.LEFT],
        nodes: new Map(),
        adapter: true,
      });
  }
  // Embedded nested chips expose their actual side-to-node bindings. A passive
  // path stays separate even when unrelated active logic exists in the chip.
  for (const tile of tiles) {
    if (tile.adapterPort || tile.adapterPin || tile.meta.bundle) continue;
    for (const [side, node] of Object.entries(tile.logicNodeIds ?? {})) {
      if (!node || !scalarParent.has(node)) continue;
      const root = scalarRoot(node);
      add({
        key: `${tile.id}:scalar:${side}`,
        tile,
        sides: [side as Side],
        channel: `scalar:${root}`,
        runtime: graph.nodes.get(node)?.runtimeKey ?? tile.id,
        scalarNodes: scalarComponents.get(root),
        embedded: tile.meta.partOfChip
          ? tile.laneNodeIds?.[side as Side]?.flatMap((node, index) => {
              const path = graph.nodes.get(node)?.bundlePath;
              return path?.length
                ? [
                    {
                      node,
                      path,
                      labels: graph.nodes.get(node)?.bundlePathLabels,
                      ids:
                        graph.nodes.get(node)?.bundleTransportIdentities ??
                        (tile.laneSignalIds?.[side as Side]?.[index] ?? []).map((id) =>
                          id.startsWith('[') ? (JSON.parse(id) as string[]) : [id],
                        ),
                      selectors: (tile.laneSignalIds?.[side as Side]?.[index] ?? []).map(
                        (id) =>
                          id.startsWith('[') ? (JSON.parse(id) as string[]) : [id],
                      ),
                    },
                  ]
                : [];
            })
          : undefined,
        nodes: new Map(),
      });
    }
  }
  // Direct member connections can leave an anonymous scalar boundary node
  // without a visible Wire tile. It is still a transport component, never a
  // genuine external driver fed by its member's own OR observation.
  const represented = new Set(vertices.map((vertex) => vertex.channel));
  for (const [root, component] of scalarComponents) {
    if (represented.has(`scalar:${root}`)) continue;
    let selected: NodeHandle | undefined;
    for (const node of component) {
      for (const edge of [...graph.getEdgesFrom(node), ...graph.getEdgesTo(node)]) {
        const outside = component.has(edge.sourceNodeId)
          ? edge.targetNodeId
          : edge.sourceNodeId;
        if (selectedTiles.has(outside)) {
          selected = outside;
          break;
        }
      }
      if (selected) break;
    }
    if (!selected) continue;
    add({
      key: `boundary:${root}`,
      tile: selectedTiles.get(selected)!,
      sides: [],
      channel: `scalar:${root}`,
      runtime: JSON.stringify([
        'member-boundary',
        selectedTiles.get(selected)!.meta.partOfChip,
        selectedLabels.get(selected),
      ]),
      scalarNodes: component,
      nodes: new Map(),
      virtual: true,
    });
  }
  const parent = new Map(vertices.map((vertex) => [vertex.key, vertex.key]));
  const find = (key: string): string => {
    let root = key;
    while (parent.get(root)! !== root) root = parent.get(root)!;
    let current = key;
    while (current !== root) {
      const next = parent.get(current)!;
      parent.set(current, root);
      current = next;
    }
    return root;
  };
  const join = (a: Vertex, b: Vertex) => {
    const ar = find(a.key),
      br = find(b.key);
    if (ar !== br) parent.set(br, ar);
  };
  const shared = new Map<string, Vertex>();
  for (const vertex of vertices) {
    const bus = vertex.adapter
      ? `adapter:${vertex.tile.meta.partOfChip}`
      : vertex.channel;
    if (!bus) continue;
    const previous = shared.get(bus);
    if (previous) join(previous, vertex);
    else shared.set(bus, vertex);
  }
  const enabled = (tile: TileInstance, side: Side) =>
    connections
      ? connections.connectionFlags(tile.id)[side]
      : (tile.meta.connections?.[side] ?? true);
  const physical: [Vertex, Vertex][] = [];
  const unmatched: { vertex: Vertex; neighbor: TileInstance; side: Side }[] = [];
  for (const vertex of vertices)
    for (const side of vertex.sides) {
      if (!enabled(vertex.tile, side)) continue;
      const direction = getGlobalDirection(side, vertex.tile.meta.rotation)!;
      const neighbor = manager.get(
        getNeighborPosition(Point.fromData(vertex.tile.location), direction),
      );
      if (
        !neighbor ||
        (vertex.tile.meta.partOfChip &&
          vertex.tile.meta.partOfChip === neighbor.meta.partOfChip)
      )
        continue;
      const neighborSide = getLocalSide(opposite(direction), neighbor.meta.rotation)!;
      const target = byTile.get(neighbor.id)?.get(neighborSide);
      if (!enabled(neighbor, neighborSide)) continue;
      if (!target) {
        unmatched.push({ vertex, neighbor, side: neighborSide });
        continue;
      }
      join(vertex, target);
      if (vertex.key < target.key) physical.push([vertex, target]);
    }
  const groups = new Map<string, Vertex[]>();
  for (const vertex of vertices) {
    const root = find(vertex.key);
    const group = groups.get(root) ?? [];
    group.push(vertex);
    groups.set(root, group);
  }
  type PayloadGroup = {
    key: string;
    vertices: Vertex[];
    roots: Map<string, Set<string>>;
    selectors: Map<string, Set<string>>;
    paths: Map<string, string[]>;
    nodes: Map<string, NodeHandle>;
    scalarNodes: Set<NodeHandle>;
  };
  const payloads = new Map<string, PayloadGroup>();
  const owners = new Map<string, string>();
  const selectedMembers = new Map<NodeHandle, { group: string; label: string }>();
  for (const [key, group] of groups) {
    const roots = new Map<string, Set<string>>();
    const selectors = new Map<string, Set<string>>();
    for (const vertex of group)
      if (vertex.adapter) {
        owners.set(vertex.tile.meta.partOfChip!, key);
        const nodes = vertex.tile.laneNodeIds?.[Side.LEFT] ?? [];
        (vertex.tile.adapterSignalIds ?? []).forEach((id, index) => {
          const label = memberKey(vertex.tile, id);
          if (!label || !nodes[index]) return;
          const aliases = roots.get(label) ?? new Set<string>();
          aliases.add(id);
          const selections = selectors.get(label) ?? new Set<string>();
          for (const alias of vertex.tile.adapterSignalAliases?.[id] ?? [])
            selections.add(alias);
          selectors.set(label, selections);
          roots.set(label, aliases);
          selectedMembers.set(nodes[index], { group: key, label });
        });
      }
    for (const vertex of group)
      for (const lane of vertex.embedded ?? []) {
        if (strict)
          lane.path.forEach((atom, index) => {
            if (lane.labels?.[index] !== undefined)
              displayLabels.set(atom, lane.labels[index]);
          });
        const ids = roots.get(lane.path[0]) ?? new Set<string>();
        for (const identity of lane.ids) if (identity[0]) ids.add(identity[0]);
        roots.set(lane.path[0], ids);
      }
    payloads.set(key, {
      key,
      vertices: group,
      roots,
      selectors,
      paths: new Map(
        [...roots.keys()].map((label) => [JSON.stringify([label]), [label]]),
      ),
      nodes: new Map(),
      scalarNodes: new Set(
        [...new Set(group.map((vertex) => vertex.scalarNodes).filter(Boolean))].flatMap(
          (nodes) => [...nodes!],
        ),
      ),
    });
  }
  type Packing = { inner: string; outer: string; member: string };
  const packings = new Map<string, Packing>();
  const attach = (inner: string, node: NodeHandle | undefined) => {
    const member = node && selectedMembers.get(node);
    if (!member) return;
    const packing = { inner, outer: member.group, member: member.label };
    packings.set(JSON.stringify([inner, member.group, member.label]), packing);
  };
  const originalEdges = [...graph.edges.values()];
  for (const group of payloads.values()) {
    for (const node of group.scalarNodes)
      for (const edge of [...graph.getEdgesFrom(node), ...graph.getEdgesTo(node)])
        attach(
          group.key,
          group.scalarNodes.has(edge.sourceNodeId)
            ? edge.targetNodeId
            : edge.sourceNodeId,
        );
  }
  for (const { vertex, neighbor, side } of unmatched)
    attach(find(vertex.key), neighbor.logicNodeIds?.[side]);
  const active = new Set<string>();
  for (const group of payloads.values()) if (group.roots.size) active.add(group.key);
  for (const relation of packings.values()) {
    active.add(relation.inner);
    active.add(relation.outer);
  }
  // A real packing cycle prefixes a member every time around the loop. Ordinary
  // gate feedback has no such shape relation and remains normal scalar feedback.
  const dependencies = new Map([...active].map((key) => [key, new Set<string>()]));
  const users = new Map([...active].map((key) => [key, new Set<string>()]));
  for (const relation of packings.values()) {
    dependencies.get(relation.outer)!.add(relation.inner);
    users.get(relation.inner)!.add(relation.outer);
  }
  const pending = [...active].filter((key) => !dependencies.get(key)!.size);
  let visitedShapes = 0;
  for (let cursor = 0; cursor < pending.length; cursor++) {
    const key = pending[cursor];
    visitedShapes++;
    for (const user of users.get(key)!) {
      dependencies.get(user)!.delete(key);
      if (!dependencies.get(user)!.size) pending.push(user);
    }
  }
  const fail = (message: string, affected: ReadonlySet<string> = active) => {
    const diagnosticNodes = new Set(
      [...affected].flatMap((key) => [...payloads.get(key)!.scalarNodes]),
    );
    for (const [node, member] of selectedMembers)
      if (affected.has(member.group)) diagnosticNodes.add(node);
    for (const tile of tiles)
      if (
        Object.values(tile.logicNodeIds ?? {}).some((node) => diagnosticNodes.has(node))
      ) {
        if (!tile.bundleErrors.includes(message)) tile.bundleErrors.push(message);
        tile.laneNodeIds = {};
        tile.laneSignalIds = {};
        tile.laneSignalLabels = {};
      }
    for (const key of affected)
      for (const vertex of payloads.get(key)!.vertices) {
        vertex.tile.bundleErrors.push(message);
        vertex.tile.laneNodeIds = {};
        vertex.tile.laneSignalIds = {};
        vertex.tile.laneSignalLabels = {};
      }
  };
  // Own only nodes touched or referenced by this rewrite. Discovery above still
  // includes every scalar equivalence class and every packing relation.
  const rewriteNodes = new Set<NodeHandle>();
  const ownNode = (node: NodeHandle) => {
    if (graph.nodes.has(node)) rewriteNodes.add(node);
  };
  if (visitedShapes !== active.size) {
    const cyclic = new Set([...active].filter((key) => dependencies.get(key)!.size));
    let changed = true;
    while (changed) {
      changed = false;
      for (const relation of packings.values())
        if (cyclic.has(relation.inner) || cyclic.has(relation.outer))
          for (const key of [relation.inner, relation.outer])
            if (!cyclic.has(key)) {
              cyclic.add(key);
              changed = true;
            }
    }
    fail(
      'Recursive bundle packing forms a cycle that would grow signal paths indefinitely. Its saved wires and adapters are preserved; disconnect the cyclic packing connection.',
      cyclic,
    );
    for (const key of cyclic)
      for (const node of payloads.get(key)!.scalarNodes) ownNode(node);
    for (const key of cyclic) active.delete(key);
    for (const [key, relation] of packings)
      if (cyclic.has(relation.inner) || cyclic.has(relation.outer)) packings.delete(key);
    for (const [node, member] of selectedMembers)
      if (cyclic.has(member.group)) {
        ownNode(node);
        const observer = graph.nodes.get(node);
        if (observer) {
          observer.type = 'DIODE';
          observer.definition = logicNodeRegistry.get('DIODE');
          observer.output = false;
        }
        for (const edge of [...graph.getEdgesFrom(node), ...graph.getEdgesTo(node)]) {
          ownNode(edge.sourceNodeId);
          ownNode(edge.targetNodeId);
          graph.removeEdge(edge.id);
        }
        selectedMembers.delete(node);
      }
  }
  const outgoing = new Map<string, Packing[]>(),
    incoming = new Map<string, Packing[]>();
  for (const relation of packings.values()) {
    outgoing.set(relation.inner, [...(outgoing.get(relation.inner) ?? []), relation]);
    incoming.set(relation.outer, [...(incoming.get(relation.outer) ?? []), relation]);
  }
  const queue: Array<{ group: string; path: string[] }> = [];
  let pathWork = 0,
    overflow = false;
  const discover = (key: string, path: string[]) => {
    const group = payloads.get(key)!,
      encoded = JSON.stringify(path);
    if (group.paths.has(encoded)) return;
    pathWork += path.length + 1;
    if (pathWork > MAX_LOWERED_GRAPH_ITEMS) {
      overflow = true;
      return;
    }
    group.paths.set(encoded, path);
    queue.push({ group: key, path });
  };
  for (const key of active)
    for (const path of payloads.get(key)!.paths.values()) {
      pathWork += path.length + 1;
      queue.push({ group: key, path });
    }
  for (const key of active)
    for (const vertex of payloads.get(key)!.vertices)
      for (const lane of vertex.embedded ?? []) discover(key, lane.path);
  for (let cursor = 0; cursor < queue.length && !overflow; cursor++) {
    const { group, path } = queue[cursor];
    for (const relation of outgoing.get(group) ?? [])
      discover(relation.outer, [relation.member, ...path]);
    for (const relation of incoming.get(group) ?? [])
      if (path[0] === relation.member) discover(relation.inner, path.slice(1));
  }
  const quarantined = new Set<NodeHandle>();
  if (strict) {
    const affected = new Set<string>();
    for (const key of active) {
      const types = new Map<string, Set<string>>();
      for (const path of payloads.get(key)!.paths.values())
        path.forEach((atom, depth) => {
          if (!atom.startsWith('adapter:[')) return;
          const [definition] = JSON.parse(atom.slice('adapter:'.length)) as string[];
          const prefix = JSON.stringify(path.slice(0, depth));
          const values = types.get(prefix) ?? new Set<string>();
          values.add(definition);
          types.set(prefix, values);
          if (values.size > 1) affected.add(key);
        });
    }
    let changed = true;
    while (changed) {
      changed = false;
      for (const relation of packings.values())
        if (affected.has(relation.inner) || affected.has(relation.outer)) {
          for (const key of [relation.inner, relation.outer])
            if (!affected.has(key)) {
              affected.add(key);
              changed = true;
            }
        }
    }
    if (affected.size) {
      fail(BUNDLE_COMPATIBILITY_MESSAGE, affected);
      for (const key of affected) {
        active.delete(key);
        for (const node of payloads.get(key)!.scalarNodes) quarantined.add(node);
        for (const vertex of payloads.get(key)!.vertices)
          for (const lane of vertex.embedded ?? []) quarantined.add(lane.node);
      }
      for (const [node, member] of selectedMembers)
        if (affected.has(member.group)) {
          quarantined.add(node);
          selectedMembers.delete(node);
        }
      for (const nodeId of quarantined) {
        const node = graph.nodes.get(nodeId);
        if (node) {
          node.type = 'DIODE';
          node.definition = logicNodeRegistry.get('DIODE');
          node.isBundleObserver = true;
          node.output = false;
        }
        for (const edge of [...graph.getEdgesFrom(nodeId), ...graph.getEdgesTo(nodeId)])
          graph.removeEdge(edge.id);
      }
      for (const [key, relation] of packings)
        if (affected.has(relation.inner) || affected.has(relation.outer))
          packings.delete(key);
      for (const key of affected) {
        outgoing.delete(key);
        incoming.delete(key);
      }
      for (const [key, values] of outgoing)
        outgoing.set(
          key,
          values.filter((relation) => !affected.has(relation.outer)),
        );
      for (const [key, values] of incoming)
        incoming.set(
          key,
          values.filter((relation) => !affected.has(relation.inner)),
        );
    }
  }
  // A scalar seed becomes a subtree when any physically connected endpoint
  // supplies children. Scalars then broadcast over those children, not a phantom
  // additional leaf. JSON arrays keep slash/bracket labels collision-free.
  for (const key of active) {
    const group = payloads.get(key)!,
      prefixes = new Set<string>();
    for (const path of group.paths.values())
      for (let length = 0; length < path.length; length++)
        prefixes.add(JSON.stringify(path.slice(0, length)));
    for (const prefix of prefixes) group.paths.delete(prefix);
  }
  // Budget the complete bundle rewrite, not unrelated ordinary logic. Every
  // existing owned edge is explicitly removed below, so its initial count and
  // removal cancel. Count all retained owned nodes plus replacement/new edges;
  // untouched edges between owned endpoints do not belong to this rewrite.
  // All active groups share this cap; display bindings are bounded separately.
  const scalarGroups = new Map<NodeHandle, string>();
  const memberCounts = new Map<string, Map<string, number>>();
  let projectedItems = 0;
  for (const key of active) {
    const group = payloads.get(key)!;
    projectedItems += group.paths.size;
    projectedItems +=
      2 * group.vertices.reduce((sum, vertex) => sum + (vertex.embedded?.length ?? 0), 0);
    for (const vertex of group.vertices)
      for (const lane of vertex.embedded ?? []) ownNode(lane.node);
    const counts = new Map<string, number>();
    for (const path of group.paths.values())
      counts.set(path[0], (counts.get(path[0]) ?? 0) + 1);
    memberCounts.set(key, counts);
    for (const node of group.scalarNodes) scalarGroups.set(node, key);
  }
  const leafCount = (node: NodeHandle) => {
    const member = selectedMembers.get(node);
    if (member) return memberCounts.get(member.group)?.get(member.label) ?? 0;
    const group = scalarGroups.get(node);
    return group ? payloads.get(group)!.paths.size : 0;
  };
  const observers = new Set([...scalarGroups.keys(), ...selectedMembers.keys()]);
  for (const node of observers) ownNode(node);
  for (const edge of originalEdges) {
    const source = observers.has(edge.sourceNodeId);
    const target = observers.has(edge.targetNodeId);
    if (!source && !target) continue;
    ownNode(edge.sourceNodeId);
    ownNode(edge.targetNodeId);
    if (source !== target)
      projectedItems += leafCount(source ? edge.sourceNodeId : edge.targetNodeId);
  }
  for (const node of observers) projectedItems += leafCount(node);
  for (const relation of packings.values())
    projectedItems += 2 * payloads.get(relation.inner)!.paths.size;
  for (const { vertex, neighbor, side } of unmatched) {
    const group = payloads.get(find(vertex.key))!;
    const node = neighbor.logicNodeIds?.[side];
    if (
      !active.has(group.key) ||
      neighbor.meta.bundle ||
      !node ||
      selectedMembers.has(node)
    )
      continue;
    const io = getEffectiveSideIO(
      neighbor,
      side,
      tileDefinitionRegistry.get(neighbor.tileType),
    );
    const directions =
      Number(
        io.type === TileSideIOType.INPUT || io.type === TileSideIOType.INPUT_OUTPUT,
      ) +
      Number(
        io.type === TileSideIOType.OUTPUT || io.type === TileSideIOType.INPUT_OUTPUT,
      );
    if (directions) ownNode(node);
    projectedItems += group.paths.size * directions;
  }
  projectedItems += rewriteNodes.size;
  if (overflow || projectedItems > MAX_LOWERED_GRAPH_ITEMS) {
    fail(
      `Bundle simulation exceeds the ${MAX_LOWERED_GRAPH_ITEMS.toLocaleString()} node/edge budget. Its saved wires and adapters are preserved; shorten or separate the connected network.`,
    );
    return;
  }
  // Carry authored IDs separately from display paths. Prefix a child's identity
  // with its actual containing member ID; reverse extraction removes that ID.
  // Renaming labels therefore changes matching/display without replacing IDs.
  const aliases = new Map<string, Map<string, Map<string, string[]>>>();
  const identityQueue: Array<{ group: string; path: string[]; ids: string[] }> = [];
  let identityWork = 0;
  const identify = (key: string, path: string[], ids: string[]) => {
    if (!payloads.get(key)!.paths.has(JSON.stringify(path))) return;
    let paths = aliases.get(key);
    if (!paths) aliases.set(key, (paths = new Map()));
    const encoded = JSON.stringify(path);
    let values = paths.get(encoded);
    if (!values) paths.set(encoded, (values = new Map()));
    const identity = JSON.stringify(ids);
    if (values.has(identity)) return;
    identityWork += ids.length + 1;
    if (identityWork > MAX_LOWERED_GRAPH_ITEMS) {
      overflow = true;
      return;
    }
    values.set(identity, ids);
    identityQueue.push({ group: key, path, ids });
  };
  for (const key of active) {
    const group = payloads.get(key)!;
    for (const [label, ids] of group.roots)
      for (const id of ids) identify(key, [label], [id]);
    if (group.paths.has('[]')) identify(key, [], []);
    for (const vertex of group.vertices)
      for (const lane of vertex.embedded ?? [])
        for (const ids of lane.ids) identify(key, lane.path, ids);
  }
  const propagateIdentities = (includeSelectors: boolean) => {
    for (let cursor = 0; cursor < identityQueue.length && !overflow; cursor++) {
      const { group, path, ids } = identityQueue[cursor];
      for (const relation of outgoing.get(group) ?? []) {
        const outer = payloads.get(relation.outer)!;
        const prefixes = [
          ...(outer.roots.get(relation.member) ?? []),
          ...(includeSelectors ? (outer.selectors.get(relation.member) ?? []) : []),
        ];
        for (const id of prefixes)
          identify(relation.outer, [relation.member, ...path], [id, ...ids]);
      }
      for (const relation of incoming.get(group) ?? [])
        if (path[0] === relation.member)
          identify(relation.inner, path.slice(1), ids.slice(1));
    }
  };
  propagateIdentities(false);
  // Freeze transport identities before scalar-selector aliases are carried.
  // Aliases never create paths, pick runtime keys, or participate in type checks.
  const transportIdentities = new Map(
    [...aliases].map(([key, paths]) => [
      key,
      new Map([...paths].map(([path, values]) => [path, [...values.values()]])),
    ]),
  );
  for (const key of active) {
    const group = payloads.get(key)!;
    for (const [member, ids] of group.selectors)
      for (const id of ids) identify(key, [member], [id]);
    for (const vertex of group.vertices)
      for (const lane of vertex.embedded ?? [])
        for (const ids of lane.selectors) identify(key, lane.path, ids);
  }
  propagateIdentities(true);
  if (overflow) {
    fail(
      `Bundle identity expansion exceeds the ${MAX_LOWERED_GRAPH_ITEMS.toLocaleString()} node/edge budget. Its saved wires and adapters are preserved; shorten or separate the connected network.`,
    );
    return;
  }
  // Binding arrays and aliases also consume memory even when their graph nodes
  // are shared. Bound the actual entries before materializing any of them.
  let bindingItems = 0;
  for (const key of active) {
    const group = payloads.get(key)!;
    let aliasEntries = 0;
    for (const encoded of group.paths.keys())
      aliasEntries += aliases.get(key)?.get(encoded)?.size ?? 0;
    bindingItems += 3 * group.paths.size + 2 * aliasEntries;
    for (const vertex of group.vertices) {
      if (
        vertex.virtual ||
        (group.paths.size === 1 && group.paths.has('[]') && !vertex.adapter)
      )
        continue;
      // Rendering still visits every visible side/leaf, even with shared arrays.
      bindingItems += vertex.sides.length * group.paths.size;
      if (bindingItems > MAX_LOWERED_GRAPH_ITEMS) {
        fail(
          `Bundle display binding budget exceeds ${MAX_LOWERED_GRAPH_ITEMS.toLocaleString()} entries. Its saved wires and adapters are preserved; shorten or separate the connected network.`,
        );
        return;
      }
    }
  }
  if (bindingItems > MAX_LOWERED_GRAPH_ITEMS) {
    fail(
      `Bundle display binding budget exceeds ${MAX_LOWERED_GRAPH_ITEMS.toLocaleString()} entries. Its saved wires and adapters are preserved; shorten or separate the connected network.`,
    );
    return;
  }
  const sortedPaths = (group: PayloadGroup) =>
    [...group.paths.entries()].sort((a, b) =>
      a[1]
        .join('\u0000')
        .localeCompare(b[1].join('\u0000'), undefined, { numeric: true }),
    );
  for (const key of active) {
    const group = payloads.get(key)!,
      paths = sortedPaths(group);
    const anchor = group.vertices
      .map((vertex) =>
        vertex.adapter
          ? `adapter:${vertex.tile.meta.partOfChip}`
          : (vertex.runtime ?? vertex.tile.id),
      )
      .sort()[0];
    for (const [encoded, path] of paths) {
      const node = graph.addNode('WIRE');
      const identity =
        (transportIdentities.get(key)?.get(encoded) ?? [])
          .map((ids) => JSON.stringify(ids))
          .sort()[0] ?? encoded;
      node.runtimeKey = JSON.stringify(['recursive-bundle', anchor, identity]);
      node.bundleTransportIdentities = transportIdentities
        .get(key)
        ?.get(encoded)
        ?.map((ids) => [...ids]);
      node.bundlePath = [...path];
      node.bundlePathLabels = path.map((key) => displayLabels.get(key) ?? key);
      group.nodes.set(encoded, node.id);
    }
    // All vertices in one transport group display exactly the same payload.
    // Already-observable arrays retain identity when assigned to observable
    // tile maps. All sides in this transport group share the same node order.
    const laneNodes = observable.array(
      paths.map(([encoded]) => group.nodes.get(encoded)!),
      { deep: false },
    );
    const labelsById: Record<string, string> = {};
    const laneIds = paths.map(([encoded, path]) =>
      [...(aliases.get(key)?.get(encoded)?.values() ?? [])].map((ids) => {
        const identity = ids.length === 1 ? ids[0] : JSON.stringify(ids);
        labelsById[identity] = displayPath(path);
        return identity;
      }),
    );
    const laneLabels = paths.map(([, path]) => displayPath(path));
    for (const vertex of group.vertices) {
      vertex.nodes = group.nodes;
      if (vertex.virtual) continue;
      // A scalar-only member wire has no visible bundle context. Its original
      // scalar observation binding still supplies ordinary brightness.
      if (paths.length === 1 && !paths[0][1].length && !vertex.adapter) continue;
      vertex.tile.laneNodeIds ??= {};
      vertex.tile.laneSignalIds ??= {};
      vertex.tile.laneSignalLabels ??= {};
      for (const side of vertex.sides) {
        vertex.tile.laneNodeIds[side] = laneNodes;
        vertex.tile.laneSignalIds[side] = laneIds;
        vertex.tile.laneSignalLabels[side] = laneLabels;
      }
      vertex.tile.bundleSignalLabels = labelsById;
      if (vertex.adapter || vertex.channel) vertex.tile.cablePort = true;
    }
  }
  // Connect child leaves by their carried label, never by aggregate brightness.
  for (const key of active) {
    const group = payloads.get(key)!;
    for (const vertex of group.vertices)
      for (const lane of vertex.embedded ?? []) {
        const leaf = group.nodes.get(JSON.stringify(lane.path));
        if (leaf) {
          graph.addEdge(leaf, lane.node);
          graph.addEdge(lane.node, leaf);
        }
      }
  }
  const memberLeafCache = new Map<string, NodeHandle[]>();
  const memberLeaves = (member: { group: string; label: string }) => {
    const key = JSON.stringify([member.group, member.label]);
    let leaves = memberLeafCache.get(key);
    if (!leaves) {
      leaves = [...payloads.get(member.group)!.nodes]
        .filter(([encoded]) => JSON.parse(encoded)[0] === member.label)
        .map(([, node]) => node);
      memberLeafCache.set(key, leaves);
    }
    return leaves;
  };
  // Capture genuine driver provenance before installing OR observations. Never
  // use an observation's HIGH as a scalar source feeding its own payload back.
  for (const edge of originalEdges) {
    if (quarantined.has(edge.sourceNodeId) || quarantined.has(edge.targetNodeId))
      continue;
    if (!observers.has(edge.sourceNodeId) && !observers.has(edge.targetNodeId)) continue;
    const sourceGroup = scalarGroups.get(edge.sourceNodeId);
    const targetGroup = scalarGroups.get(edge.targetNodeId);
    const sourceMember = selectedMembers.get(edge.sourceNodeId),
      targetMember = selectedMembers.get(edge.targetNodeId);
    graph.removeEdge(edge.id);
    if ((sourceGroup || sourceMember) && (targetGroup || targetMember)) continue;
    if (sourceGroup || sourceMember) {
      const leaves = sourceMember
        ? memberLeaves(sourceMember)
        : [...payloads.get(sourceGroup!)!.nodes.values()];
      for (const leaf of leaves) graph.addEdge(leaf, edge.targetNodeId, edge.meta);
    } else if (targetGroup || targetMember) {
      const leaves = targetMember
        ? memberLeaves(targetMember)
        : [...payloads.get(targetGroup!)!.nodes.values()];
      for (const leaf of leaves) graph.addEdge(edge.sourceNodeId, leaf, edge.meta);
    }
  }
  for (const nodeId of observers) {
    const node = graph.nodes.get(nodeId);
    if (!node) continue;
    node.isBundleObserver = true;
    node.type = 'DIODE';
    node.definition = logicNodeRegistry.get('DIODE');
    const member = selectedMembers.get(nodeId);
    const group = !member && scalarGroups.get(nodeId);
    const leaves = member
      ? memberLeaves(member)
      : group
        ? [...payloads.get(group)!.nodes.values()]
        : [];
    for (const leaf of leaves) graph.addEdge(leaf, nodeId);
  }
  for (const relation of packings.values()) {
    const inner = payloads.get(relation.inner)!,
      outer = payloads.get(relation.outer)!;
    for (const [encoded, node] of inner.nodes) {
      const target = outer.nodes.get(
        JSON.stringify([relation.member, ...JSON.parse(encoded)]),
      );
      if (target) {
        graph.addEdge(node, target);
        graph.addEdge(target, node);
      }
    }
  }
  for (const { vertex, neighbor, side } of unmatched) {
    const group = payloads.get(find(vertex.key))!;
    if (!active.has(group.key)) continue;
    if (neighbor.meta.bundle) {
      const error =
        'A legacy fixed-lane bundle cannot directly join an inferred bundle. Keep its legacy interfaces or redraw the adapter-only wire path.';
      vertex.tile.bundleErrors.push(error);
      neighbor.bundleErrors.push(error);
      continue;
    }
    const node = neighbor.logicNodeIds?.[side];
    if (!node || selectedMembers.has(node) || quarantined.has(node)) continue;
    const io = getEffectiveSideIO(
      neighbor,
      side,
      tileDefinitionRegistry.get(neighbor.tileType),
    );
    for (const leaf of group.nodes.values()) {
      if (io.type === TileSideIOType.INPUT || io.type === TileSideIOType.INPUT_OUTPUT)
        graph.addEdge(leaf, node);
      if (io.type === TileSideIOType.OUTPUT || io.type === TileSideIOType.INPUT_OUTPUT)
        graph.addEdge(node, leaf);
    }
  }
  return [...active].some((key) =>
    [...payloads.get(key)!.paths.values()].some((path) => path.length > 1),
  );
}
